import React, { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Shield, Home, Users, BarChart3, Trophy, ClipboardList, Settings } from 'lucide-react';

export default function Layout({ children }) {
  const { user, logout, seasons, currentSeason, setCurrentSeason, tenantContext, isSuperAdmin } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine user's role in the tenant
  const tenantRole = tenantContext?.membership?.role || user?.role;
  const isLeagueAdmin = tenantRole === 'league_admin' || user?.role === 'admin';
  const isModerator = tenantRole === 'moderator';
  const canAccessAdmin = isLeagueAdmin || isModerator;
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-stone-50 pb-24 lg:pb-0">
      {/* Top Navigation */}
      <nav className="text-white shadow-lg border-b-4" style={{ background: 'linear-gradient(to right, #1E3A4C, #2a4a5c)', borderColor: '#FF7F50' }}>
        <div className="container-mobile">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <Link to="/home" className="flex items-center space-x-2 sm:space-x-3">
              <img 
                src="/logo-new.png" 
                alt="Survivor Fantasy League" 
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
              />
              <span className="text-base sm:text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Survivor League
              </span>
            </Link>

            {/* Main Navigation - Hidden on mobile */}
            <div className="hidden lg:flex items-center space-x-1">
              <NavLink to="/home" active={isActive('/home')}>Home</NavLink>
              <NavLink to="/my-roster" active={isActive('/my-roster')}>Roster</NavLink>
              <NavLink to="/scorecard" active={isActive('/scorecard')}>Scorecard</NavLink>
              <NavLink to="/leaderboard" active={isActive('/leaderboard')}>Leaderboard</NavLink>
              <NavLink to="/event-log" active={isActive('/event-log')}>Event Log</NavLink>
              {canAccessAdmin && (
                <NavLink to="/admin" active={isActive('/admin')}>Admin</NavLink>
              )}
              {isSuperAdmin && (
                <NavLink to="/super-admin" active={isActive('/super-admin')}>
                  <Shield className="w-4 h-4 mr-1 inline" />
                  Platform
                </NavLink>
              )}
            </div>

            {/* Right side - Season Selector & User Menu */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Season Selector - Hidden on mobile */}
              {seasons.length > 0 && currentSeason && (
                <div className="hidden lg:block">
                  <Select
                    value={currentSeason?.id}
                    onValueChange={(value) => {
                      const season = seasons.find(s => s.id === value);
                      setCurrentSeason(season);
                    }}
                  >
                    <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select season" />
                    </SelectTrigger>
                    <SelectContent>
                      {seasons.map(season => (
                        <SelectItem key={season.id} value={season.id}>
                          Season {season.number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center space-x-1 sm:space-x-2 hover:bg-slate-700 px-2 sm:px-3 py-2 rounded-lg transition-colors" data-testid="user-menu">
                    {user?.avatar_url ? (
                      <img src={user.avatar_url} alt={user.name} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full" />
                    ) : (
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-amber-500 rounded-full flex items-center justify-center font-bold text-sm">
                        {user?.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <span className="hidden lg:block text-sm">{user?.name}</span>
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm">{user?.name}</span>
                      <span className="text-xs text-slate-500">{user?.email}</span>
                      <span className="text-xs text-amber-600 font-medium mt-1">
                        {isSuperAdmin ? 'Super Admin' : 
                         isLeagueAdmin ? 'League Admin' : 
                         isModerator ? 'Moderator' : 'Player'}
                      </span>
                      {tenantContext?.tenant && (
                        <span className="text-xs text-slate-400 mt-0.5">
                          {tenantContext.tenant.name}
                        </span>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  {/* Mobile Season Selector */}
                  {seasons.length > 0 && currentSeason && (
                    <>
                      <div className="lg:hidden px-2 py-2">
                        <label className="text-xs font-medium text-slate-700 mb-1 block">Season</label>
                        <Select
                          value={currentSeason?.id}
                          onValueChange={(value) => {
                            const season = seasons.find(s => s.id === value);
                            setCurrentSeason(season);
                          }}
                        >
                          <SelectTrigger className="w-full h-8">
                            <SelectValue placeholder="Select season" />
                          </SelectTrigger>
                          <SelectContent>
                            {seasons.map(season => (
                              <SelectItem key={season.id} value={season.id}>
                                Season {season.number}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="lg:hidden">
                        <DropdownMenuSeparator />
                      </div>
                    </>
                  )}

                  {/* Admin / Platform Quick Links (shown on all sizes when applicable) */}
                  {canAccessAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin-dashboard">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  {isSuperAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/super-admin')} data-testid="menu-platform-admin">
                      <Shield className="w-4 h-4 mr-2" />
                      Platform Admin
                    </DropdownMenuItem>
                  )}
                  {(canAccessAdmin || isSuperAdmin) && <DropdownMenuSeparator />}

                  {/* Notifications */}
                  <DropdownMenuItem onClick={() => navigate('/email-preferences')} data-testid="menu-notifications">
                    🔔 Notifications
                  </DropdownMenuItem>
                  {/* Contact Us - only for non-Super Admin */}
                  {!isSuperAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/contact')} data-testid="menu-contact">
                      💬 Contact Us
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="menu-signout">
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Impersonation Banner */}
      {user?.impersonated_by && (
        <div className="bg-blue-600 text-white py-2 px-4">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">
                Viewing as {user.name} • Impersonated by {user.impersonated_by.admin_name}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={logout}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20"
            >
              Exit Impersonation
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container-mobile py-4 md:py-6 lg:py-8">
        {children}
      </main>

      {/* Mobile Bottom Navigation (logged-in users) - Liquid Glass */}
      <div
        className="lg:hidden fixed left-3 right-3 z-50 rounded-full overflow-hidden"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom) + 12px)',
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          boxShadow:
            '0 10px 30px rgba(30, 58, 76, 0.18), 0 2px 6px rgba(30, 58, 76, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        }}
        data-testid="app-mobile-bottom-nav"
      >
        <div className="flex items-center justify-around py-2 px-1">
          <MobileTab to="/home" icon={Home} label="Home" active={isActive('/home')} />
          <MobileTab to="/my-roster" icon={Users} label="Roster" active={isActive('/my-roster')} />
          <MobileTab to="/scorecard" icon={BarChart3} label="Score" active={isActive('/scorecard')} />
          <MobileTab to="/leaderboard" icon={Trophy} label="Leaders" active={isActive('/leaderboard')} />
          {canAccessAdmin ? (
            <MobileTab to="/admin" icon={Settings} label="Admin" active={isActive('/admin')} />
          ) : (
            <MobileTab to="/event-log" icon={ClipboardList} label="Events" active={isActive('/event-log')} />
          )}
        </div>
      </div>
    </div>
  );
}

function NavLink({ to, active, children }) {
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-lg text-sm lg:text-base font-medium transition-colors ${
        active
          ? 'bg-amber-500 text-white'
          : 'hover:bg-slate-700 text-slate-200'
      }`}
    >
      {children}
    </Link>
  );
}

function MobileTab({ to, icon: Icon, label, active }) {
  return (
    <Link
      to={to}
      className={`flex flex-col items-center px-3 py-1 rounded-lg transition-colors ${
        active
          ? 'text-[#FF7F50]'
          : 'text-[#1E3A4C]/70 hover:text-[#1E3A4C]'
      }`}
      data-testid={`app-mobile-tab-${label.toLowerCase()}`}
    >
      <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
      <span className={`text-xs mt-0.5 ${active ? 'font-semibold' : ''}`}>{label}</span>
    </Link>
  );
}
